import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUIStore } from './ui'
import apiService from '../services/apiService'
import logService from '../services/logService'

export const useContentStore = defineStore('content', () => {
  const uiStore = useUIStore()

  // State
  const content = ref({})
  const pageSections = ref({})
  const loading = ref(false)

  // API Functions
  const fetchAllContent = async () => {
    try {
      loading.value = true
      logService.info('Fetching all content...')

      const response = await apiService.get('/content')

      if (response.success) {
        content.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch content')
      }
    } catch (error) {
      logService.error('Fetch All Content Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return {}
    } finally {
      loading.value = false
    }
  }

  const fetchContentSection = async (section) => {
    try {
      logService.info(`Fetching content section: ${section}`)

      const response = await apiService.get(`/content/${section}`)

      if (response.success) {
        content.value[section] = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch content section')
      }
    } catch (error) {
      logService.error(`Fetch Content Section Error [${section}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return null
    }
  }

  const updateContentSection = async (section, contentData) => {
    try {
      loading.value = true
      logService.info(`Updating content section: ${section}`, contentData)

      const response = await apiService.put(`/content/${section}`, { content: contentData })

      if (response.success) {
        content.value[section] = response.data

        uiStore.showNotification({
          type: 'success',
          title: 'Content Updated',
          message: `${section} section has been updated successfully.`
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to update content')
      }
    } catch (error) {
      logService.error(`Update Content Section Error [${section}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  // Page Sections Functions
  const fetchPageSections = async (page) => {
    try {
      logService.info(`Fetching page sections for page: ${page}`)

      const response = await apiService.get('/page-sections', { page })

      if (response.success) {
        pageSections.value[page] = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch page sections')
      }
    } catch (error) {
      logService.error(`Fetch Page Sections Error [${page}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return []
    }
  }

  const createPageSection = async (sectionData) => {
    try {
      loading.value = true
      logService.info('Creating page section...', sectionData)

      const response = await apiService.post('/page-sections', sectionData)

      if (response.success) {
        // Update in local state
        const page = response.data.page
        if (!pageSections.value[page]) {
          pageSections.value[page] = []
        }
        pageSections.value[page].push(response.data)

        uiStore.showNotification({
          type: 'success',
          title: 'Section Created',
          message: 'Page section has been created successfully.'
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to create page section')
      }
    } catch (error) {
      logService.error('Create Page Section Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Creation Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const updatePageSection = async (id, sectionData) => {
    try {
      loading.value = true
      logService.info(`Updating page section: ${id}`, sectionData)

      const response = await apiService.put(`/page-sections/${id}`, sectionData)

      if (response.success) {
        // Update in local state
        const page = response.data.page
        if (pageSections.value[page]) {
          const index = pageSections.value[page].findIndex(s => s.id === id)
          if (index !== -1) {
            pageSections.value[page][index] = response.data
          }
        }

        uiStore.showNotification({
          type: 'success',
          title: 'Section Updated',
          message: 'Page section has been updated successfully.'
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to update page section')
      }
    } catch (error) {
      logService.error(`Update Page Section Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const uploadSectionImage = async (sectionId, imageFile) => {
    try {
      loading.value = true
      logService.info(`Uploading image for section: ${sectionId}`)

      const formData = new FormData()
      formData.append('image', imageFile)

      const response = await apiService.upload(`/page-sections/${sectionId}/upload-image`, formData)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Image Uploaded',
          message: 'Section image has been uploaded successfully.'
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to upload image')
      }
    } catch (error) {
      logService.error(`Upload Section Image Error [${sectionId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Upload Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  // Getters
  const getContentSection = (section) => {
    return content.value[section] || {}
  }

  const getHeroContent = () => {
    return getContentSection('hero')
  }

  const getAboutContent = () => {
    return getContentSection('about')
  }

  const getContactContent = () => {
    return getContentSection('contact')
  }

  const getServicesIntroContent = () => {
    return getContentSection('services_intro')
  }

  return {
    // State
    content,
    pageSections,
    loading,

    // API Actions
    fetchAllContent,
    fetchContentSection,
    updateContentSection,
    fetchPageSections,
    createPageSection,
    updatePageSection,
    uploadSectionImage,

    // Getters
    getContentSection,
    getHeroContent,
    getAboutContent,
    getContactContent,
    getServicesIntroContent
  }
})
